import React, { useState } from 'react';
import { ShieldAlert, RefreshCw, ChevronDown, ChevronRight, AlertTriangle, AlertCircle, Info, CheckCircle } from 'lucide-react';
import { dataIntegrityService, DataIntegrityCheckResult, IntegrityCategory } from '../../../../services/dataIntegrityService';

export const DataIntegrity: React.FC = () => {
    const [result, setResult] = useState<DataIntegrityCheckResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>('');
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

    const runAnalysis = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await dataIntegrityService.validate();
            setResult(data);
            // Auto-expand categories with issues
            const withIssues = new Set(
                data.categories.filter(c => c.issues.length > 0).map(c => c.name)
            );
            setExpandedCategories(withIssues);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'En feil oppstod');
        } finally {
            setLoading(false);
        }
    };

    const toggleCategory = (name: string) => {
        setExpandedCategories(prev => {
            const next = new Set(prev);
            if (next.has(name)) {
                next.delete(name);
            } else {
                next.add(name);
            }
            return next;
        });
    };

    const severityConfig = {
        critical: { label: 'Kritisk', color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-800', icon: AlertCircle },
        warning: { label: 'Advarsel', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-800', icon: AlertTriangle },
        info: { label: 'Info', color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-800', icon: Info },
    };

    const renderSummaryCards = () => {
        if (!result) return null;

        const categoriesWithIssues = result.categories.filter(c => c.issues.length > 0);
        const criticalCategories = categoriesWithIssues.filter(c => c.severity === 'critical');
        const warningCategories = categoriesWithIssues.filter(c => c.severity === 'warning');
        const cleanCategories = result.categories.filter(c => c.issues.length === 0);

        return (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className={`rounded-lg p-4 border ${criticalCategories.length > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center gap-2 mb-1">
                        <AlertCircle className={`w-5 h-5 ${criticalCategories.length > 0 ? 'text-red-600' : 'text-gray-400'}`} />
                        <span className={`text-2xl font-bold ${criticalCategories.length > 0 ? 'text-red-700' : 'text-gray-500'}`}>
                            {criticalCategories.length}
                        </span>
                    </div>
                    <p className={`text-sm ${criticalCategories.length > 0 ? 'text-red-600' : 'text-gray-500'}`}>Kritiske</p>
                </div>

                <div className={`rounded-lg p-4 border ${warningCategories.length > 0 ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center gap-2 mb-1">
                        <AlertTriangle className={`w-5 h-5 ${warningCategories.length > 0 ? 'text-amber-600' : 'text-gray-400'}`} />
                        <span className={`text-2xl font-bold ${warningCategories.length > 0 ? 'text-amber-700' : 'text-gray-500'}`}>
                            {warningCategories.length}
                        </span>
                    </div>
                    <p className={`text-sm ${warningCategories.length > 0 ? 'text-amber-600' : 'text-gray-500'}`}>Advarsler</p>
                </div>

                <div className={`rounded-lg p-4 border ${cleanCategories.length > 0 ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center gap-2 mb-1">
                        <CheckCircle className={`w-5 h-5 ${cleanCategories.length > 0 ? 'text-green-600' : 'text-gray-400'}`} />
                        <span className={`text-2xl font-bold ${cleanCategories.length > 0 ? 'text-green-700' : 'text-gray-500'}`}>
                            {cleanCategories.length}
                        </span>
                    </div>
                    <p className={`text-sm ${cleanCategories.length > 0 ? 'text-green-600' : 'text-gray-500'}`}>OK</p>
                </div>
            </div>
        );
    };

    const renderCategory = (category: IntegrityCategory) => {
        const isExpanded = expandedCategories.has(category.name);
        const hasIssues = category.issues.length > 0;
        const config = severityConfig[category.severity] || severityConfig.info;
        const SeverityIcon = config.icon;

        return (
            <div
                key={category.name}
                className={`rounded-lg border ${hasIssues ? config.border : 'border-gray-200'} overflow-hidden`}
            >
                <button
                    onClick={() => toggleCategory(category.name)}
                    className={`w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors ${hasIssues ? config.bg : 'bg-white'}`}
                >
                    <div className="flex items-center gap-3 flex-1">
                        {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
                        ) : (
                            <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
                        )}
                        <div className="flex-1">
                            <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900">{category.name}</span>
                                <span className="text-xs text-gray-500">
                                    ({category.sourceTable} → {category.referencedTable})
                                </span>
                            </div>
                            <p className="text-sm text-gray-500 mt-0.5">{category.description}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                        {hasIssues ? (
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${config.badge}`}>
                                <SeverityIcon className="w-3 h-3" />
                                {category.issues.reduce((sum, i) => sum + i.affectedRows, 0)} rader
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <CheckCircle className="w-3 h-3" />
                                OK
                            </span>
                        )}
                    </div>
                </button>

                {isExpanded && hasIssues && (
                    <div className="border-t border-gray-200">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50">
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Kolonne</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ugyldig verdi</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Berørte rader</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Eksempler</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Konsekvens</th>
                                </tr>
                            </thead>
                            <tbody>
                                {category.issues.map((issue, idx) => (
                                    <tr key={idx} className="border-t border-gray-100 hover:bg-gray-50">
                                        <td className="px-4 py-3 font-mono text-xs">{issue.column}</td>
                                        <td className="px-4 py-3">
                                            <span className="inline-flex px-2 py-0.5 rounded bg-red-100 text-red-800 font-mono text-xs">
                                                {issue.invalidValue}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right font-medium">{issue.affectedRows}</td>
                                        <td className="px-4 py-3 text-xs text-gray-600 max-w-xs truncate">
                                            {issue.exampleRecords.join(', ')}
                                        </td>
                                        <td className="px-4 py-3 text-xs text-gray-600">{issue.impact}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {isExpanded && !hasIssues && (
                    <div className="border-t border-gray-200 p-4 text-sm text-gray-500 text-center">
                        Ingen problemer funnet for denne sjekken.
                    </div>
                )}
            </div>
        );
    };

    // Sort: categories with issues first, then by severity
    const sortedCategories = result
        ? [...result.categories].sort((a, b) => {
            const aHas = a.issues.length > 0 ? 0 : 1;
            const bHas = b.issues.length > 0 ? 0 : 1;
            if (aHas !== bHas) return aHas - bHas;
            const sevOrder: Record<string, number> = { critical: 0, warning: 1, info: 2 };
            return (sevOrder[a.severity] ?? 3) - (sevOrder[b.severity] ?? 3);
        })
        : [];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-700 to-orange-600 text-white p-6 rounded-lg">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <ShieldAlert className="w-8 h-8" />
                            <h1 className="text-2xl font-bold">Dataintegritet</h1>
                        </div>
                        <p className="text-red-100">
                            Analyser datakvalitet og relasjonsintegritet. Finner manglende referanser som kan forårsake at regelmotor stille ignorerer data.
                        </p>
                    </div>
                    <button
                        onClick={runAnalysis}
                        disabled={loading}
                        className="flex items-center gap-2 px-5 py-2.5 bg-white text-red-700 rounded-lg font-medium hover:bg-red-50 disabled:opacity-50 transition-colors flex-shrink-0"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        {loading ? 'Analyserer...' : 'Kjør analyse'}
                    </button>
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
                    {error}
                </div>
            )}

            {/* Summary Cards */}
            {renderSummaryCards()}

            {/* Results */}
            {result && (
                <div className="space-y-3">
                    <h2 className="text-lg font-semibold text-gray-900">
                        Detaljerte resultater ({result.totalIssues} problemer i {result.categories.filter(c => c.issues.length > 0).length} av {result.categories.length} sjekker)
                    </h2>
                    {sortedCategories.map(renderCategory)}
                </div>
            )}

            {/* Empty state */}
            {!result && !loading && !error && (
                <div className="text-center py-16 text-gray-500">
                    <ShieldAlert className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg mb-2">Ingen analyse kjørt ennå</p>
                    <p className="text-sm">Klikk "Kjør analyse" for å sjekke dataintegritet</p>
                </div>
            )}
        </div>
    );
};
